import Badge from "@mui/material/Badge";
import { memo } from "react";
import { useLocation } from "react-router-dom";

import AppIcon from "@/components/Core/AppIcon";
import HeaderIconButton from "@/components/Core/HeaderIconButton";
import { useTranslate } from "@/hooks";
import { useDispatch, useSelector } from "@/store";
import { setChatRoom } from "@/store/reducers/auth";
import { toggleBetSlip } from "@/store/reducers/betSlip";
import { colors } from "@/theme";

/** Where the sportsbook's pages live. */
const SPORT_PATH_PREFIX = "/sports";

/**
 * The bet slip's button in the header, for screens wide enough to have no
 * footer bar.
 *
 * Once the slip was closed there was no way to open it again short of adding
 * or removing a pick: its only opener lived in the phone footer. This is the
 * desktop's, lit while the slip is open and carrying the number of picks in
 * it. It is in the header in the sportsbook, and anywhere else while there is
 * something in the slip.
 */
const BetSlipButton = () => {
  const t = useTranslate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const open = useSelector((s) => s.betSlip.isOpen);
  const count = useSelector((s) => s.betSlip.bets.length);
  const chatOpen = useSelector((s) => s.auth.chatRoomOpen);

  if (!pathname.startsWith(SPORT_PATH_PREFIX) && count === 0) return null;

  const handleClick = () => {
    // The slip and the chat share the right-hand drawer: one gives way to the other.
    if (!open && chatOpen) dispatch(setChatRoom(false));
    dispatch(toggleBetSlip());
  };

  return (
    <HeaderIconButton
      label={t("bet-slip.title")}
      active={open}
      aria-pressed={open}
      onClick={handleClick}
    >
      <Badge
        badgeContent={count}
        max={99}
        sx={{
          "& .MuiBadge-badge": {
            top: -3,
            right: -3,
            minWidth: 16,
            height: 16,
            px: 0.5,
            fontSize: 10,
            fontWeight: 700,
            // Readable on the filled button too, where a blue badge would vanish.
            color: open ? colors.prime : colors.white,
            backgroundColor: open ? colors.white : colors.prime,
          },
        }}
      >
        <AppIcon name="document2" size={18} />
      </Badge>
    </HeaderIconButton>
  );
};

export default memo(BetSlipButton);
